import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import CommunityContent from './CommunityContent';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Users, Calendar, Bell, AlertCircle, 
  Mail, HandHeart, ChevronRight, Clock, CheckCircle,
  MapPin, CreditCard
} from 'lucide-react';
import { format, formatDistanceToNow, isToday, isTomorrow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [commStats, setCommStats] = useState(null);
  const [volunteerStats, setVolunteerStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [dashRes, commRes, volRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/parent`, { withCredentials: true }),
        axios.get(`${API_URL}/api/communications/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/volunteer/stats`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setDashboard(dashRes.data);
      setCommStats(commRes.data);
      setVolunteerStats(volRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPlayers = dashboard?.players?.length || 0;
  const clearedPlayers = dashboard?.players?.filter(p => 
    p.eligibility_status === 'eligible' || p.eligibility_status === 'cleared'
  ).length || 0;
  const unreadMessages = (commStats?.unread_announcements || 0) + (commStats?.unread_messages || 0);

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-navy/20 rounded-xl"></div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
            <div className="h-72 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="parent-dashboard">
        {/* Welcome Banner */}
        <div className="rounded-xl p-6 sm:p-8 mb-6 text-white relative overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('/field-bg-2.jpeg')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-navy/[0.01] via-navy/[0.03] to-navy/[0.05]" />
          <div className="absolute inset-0 bg-navy/70" />
          <div className="absolute top-0 right-0 w-48 h-48 bg-gold/10 rounded-full -translate-y-24 translate-x-24"></div>
          <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-red/10 rounded-full translate-y-16"></div>
          
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Welcome Back</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-3">
              {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-white/70 text-sm sm:text-base font-body max-w-lg">
              {totalPlayers > 0 
                ? `You have ${totalPlayers} player${totalPlayers > 1 ? 's' : ''} registered with the Park City Chiefs.`
                : 'Get started by registering your players for the upcoming season.'}
            </p>
            {dashboard?.balance > 0 && (
              <div className="mt-5 inline-flex items-center bg-red/20 border border-red/40 rounded-lg px-4 py-2.5">
                <AlertCircle className="w-5 h-5 text-red mr-2" />
                <span className="text-sm font-semibold">Balance due: <span className="text-gold">${dashboard.balance.toFixed(2)}</span></span>
              </div>
            )}
          </div>
        </div>

        {/* 6 Stat Widgets */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6" data-testid="stat-widgets">
          {/* My Players */}
          <div 
            onClick={() => navigate('/family')}
            className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-navy/30 transition-all duration-200"
            data-testid="widget-my-players"
          >
            <div className="w-8 h-8 bg-navy/10 rounded-lg flex items-center justify-center mb-2">
              <Users className="w-4 h-4 text-navy" />
            </div>
            <p className="font-display text-2xl sm:text-3xl font-black text-navy tracking-tight">{totalPlayers}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">My Players</p>
          </div>

          {/* Upcoming Events */}
          <div 
            onClick={() => navigate('/schedule')}
            className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-light-blue/30 transition-all duration-200"
            data-testid="widget-upcoming"
          >
            <div className="w-8 h-8 bg-light-blue/10 rounded-lg flex items-center justify-center mb-2">
              <Calendar className="w-4 h-4 text-light-blue" />
            </div>
            <p className="font-display text-2xl sm:text-3xl font-black text-light-blue tracking-tight">{dashboard?.events?.length || 0}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">Upcoming</p>
          </div>

          {/* Messages */}
          <div 
            onClick={() => navigate('/communications')}
            className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-light-blue/30 transition-all duration-200 relative"
            data-testid="widget-messages"
          >
            {unreadMessages > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                {unreadMessages}
              </span>
            )}
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mb-2">
              <Mail className="w-4 h-4 text-purple-600" />
            </div>
            <p className="font-display text-2xl sm:text-3xl font-black text-purple-600 tracking-tight">{unreadMessages}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">Messages</p>
          </div>

          {/* Volunteer */}
          <div 
            onClick={() => navigate('/volunteer')}
            className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-gold/30 transition-all duration-200 relative"
            data-testid="widget-volunteer"
          >
            {volunteerStats?.my_commitments > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                {volunteerStats.my_commitments}
              </span>
            )}
            <div className="w-8 h-8 bg-gold/20 rounded-lg flex items-center justify-center mb-2">
              <HandHeart className="w-4 h-4 text-gold" />
            </div>
            <p className="font-display text-2xl sm:text-3xl font-black text-gold tracking-tight">{volunteerStats?.open_opportunities || 0}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">Volunteer</p>
          </div>

          {/* Cleared */}
          <div 
            className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
            data-testid="widget-cleared"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
              clearedPlayers === totalPlayers && totalPlayers > 0 ? 'bg-green-100' : 'bg-gold/20'
            }`}>
              {clearedPlayers === totalPlayers && totalPlayers > 0 
                ? <CheckCircle className="w-4 h-4 text-green-600" />
                : <Clock className="w-4 h-4 text-gold" />
              }
            </div>
            <p className={`font-display text-2xl sm:text-3xl font-black tracking-tight ${
              clearedPlayers === totalPlayers && totalPlayers > 0 ? 'text-green-600' : 'text-gold'
            }`}>{clearedPlayers}/{totalPlayers}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">Cleared</p>
          </div>

          {/* Balance */}
          <div 
            className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
            data-testid="widget-balance"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
              dashboard?.balance > 0 ? 'bg-red/10' : 'bg-green-100'
            }`}>
              <CreditCard className={`w-4 h-4 ${dashboard?.balance > 0 ? 'text-red' : 'text-green-600'}`} />
            </div>
            <p className={`font-display text-2xl sm:text-3xl font-black tracking-tight ${
              dashboard?.balance > 0 ? 'text-red' : 'text-green-600'
            }`}>${dashboard?.balance?.toFixed(0) || '0'}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">Balance</p>
          </div>
        </div>

        {/* Quick Peek — Next Events */}
        {dashboard?.events?.length > 0 && (
          <div className="mb-6" data-testid="quick-peek-events">
            <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {dashboard.events.slice(0, 3).map((event) => {
                const eventDate = new Date(event.start_time);
                return (
                  <div
                    key={event.event_id}
                    onClick={() => navigate('/schedule')}
                    className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2.5 cursor-pointer hover:shadow-sm hover:border-navy/20 transition-all flex-shrink-0 min-w-[200px]"
                    data-testid={`peek-event-${event.event_id}`}
                  >
                    <div className="text-center min-w-[36px]">
                      <p className="text-lg font-black text-navy leading-none">{format(eventDate, 'd')}</p>
                      <p className="text-[9px] text-gray-400 uppercase">{format(eventDate, 'EEE')}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-navy truncate">{event.title}</p>
                      <p className="text-[10px] text-gray-400">{format(eventDate, 'h:mm a')}{event.location_name ? ` · ${event.location_name}` : ''}</p>
                    </div>
                  </div>
                );
              })}
              {dashboard.events.length > 3 && (
                <button
                  onClick={() => navigate('/schedule')}
                  className="flex items-center px-3 py-2.5 text-xs font-bold text-navy hover:text-red transition-colors flex-shrink-0"
                >
                  +{dashboard.events.length - 3} more <ChevronRight className="w-3 h-3 ml-1" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Recent Announcements - Top 3 */}
        {dashboard?.announcements && dashboard.announcements.length > 0 && (
          <div className="mb-6" data-testid="recent-announcements">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                <div className="flex items-center">
                  <Bell className="w-4 h-4 text-navy mr-2" />
                  <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Recent Announcements</h2>
                  {commStats?.unread_announcements > 0 && (
                    <span className="ml-2 bg-red text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {commStats.unread_announcements} new
                    </span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/communications')}
                  className="text-navy hover:text-red hover:bg-transparent font-semibold text-xs"
                >
                  View All <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="p-4 space-y-3">
                {dashboard.announcements.slice(0, 3).map((announcement) => (
                  <div
                    key={announcement.announcement_id}
                    className="p-3 border border-gray-100 rounded-lg hover:border-navy/20 hover:shadow-sm transition-all duration-200 cursor-pointer"
                    onClick={() => navigate('/communications')}
                    data-testid={`announcement-${announcement.announcement_id}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-bold text-navy text-sm">{announcement.title}</h3>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        {announcement.priority === 'high' && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red text-white">
                            Urgent
                          </span>
                        )}
                        <span className="text-[11px] text-gray-400">
                          {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{announcement.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Events needing RSVP */}
        {dashboard?.events?.filter(e => !e.has_rsvp).length > 0 && (
          <div className="mb-6" data-testid="rsvp-banner">
            <div className="bg-red/5 border-l-4 border-red rounded-r-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red mr-3" />
                <div>
                  <p className="font-semibold text-navy text-sm">
                    {dashboard.events.filter(e => !e.has_rsvp).length} event{dashboard.events.filter(e => !e.has_rsvp).length > 1 ? 's' : ''} need your RSVP
                  </p>
                  <p className="text-xs text-gray-600">Let your coaches know if your player can attend.</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/schedule')}
                className="bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider text-xs"
              >
                View
              </Button>
            </div>
          </div>
        )}

        {/* Community Feed */}
        <div
          className="relative rounded-xl overflow-hidden p-4 sm:p-6"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_d8bf1b61-a9b7-4610-b686-61792bda2646/artifacts/h4iga2z1_App_icon-Background.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
          data-testid="dashboard-community-feed"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Community Feed</h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/community')}
              className="text-navy hover:text-red hover:bg-transparent font-semibold text-sm"
            >
              Full Feed <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <CommunityContent embedded={true} />
        </div>
      </div>
    </ParentLayout>
  );
};

export default ParentDashboard;
